import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RefreshCw, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminRsvp() {
  const { t } = useLang();
  const [rsvps, setRsvps] = useState([]);
  const [stats, setStats] = useState({ total_responses: 0, attending: 0, declined: 0, total_guests: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/rsvp`),
        axios.get(`${API}/rsvp/stats`),
      ]);
      setRsvps(r.data || []);
      setStats(s.data || {});
    } catch (err) {
      toast.error(t("admin.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    try {
      await axios.delete(`${API}/rsvp/${id}`);
      toast.success(t("admin.deleted"));
      load();
    } catch {
      toast.error(t("admin.deleteFailed"));
    }
  };

  const fmtDate = (s) => {
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };

  const heading = t("admin.heading");
  const cols = t("admin.table");
  const statLbl = t("admin.stats");

  return (
    <div data-testid={ADMIN.page} className="min-h-screen bg-[color:var(--paper)] text-[color:var(--ink)]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-16">
        <div className="flex items-center justify-between mb-12 gap-4 flex-wrap">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 font-overline text-[10px] text-black/60 hover:text-black mb-4"
            >
              <ArrowLeft size={14} /> {t("admin.back")}
            </Link>
            <div className="font-overline text-[11px] text-[color:var(--sunset-600)]">{t("admin.private")}</div>
            <h1 className="mt-2 font-display text-5xl sm:text-6xl tracking-tighter font-light">
              {heading[0]}<em className="italic">{heading[1]}</em>{heading[2]}
            </h1>
          </div>
          <button
            data-testid={ADMIN.refresh}
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 border border-black bg-black text-white px-5 py-3 font-overline text-[11px] hover:bg-transparent hover:text-black transition-colors disabled:opacity-60"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            {t("admin.refresh")}
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10 mb-12">
          {[
            { id: ADMIN.stats.total, label: statLbl.responses, value: stats.total_responses },
            { id: ADMIN.stats.attending, label: statLbl.attending, value: stats.attending },
            { id: ADMIN.stats.declined, label: statLbl.declined, value: stats.declined },
            { id: ADMIN.stats.guests, label: statLbl.guests, value: stats.total_guests },
          ].map((s, i) => (
            <div key={i} data-testid={s.id} className="bg-white p-6 sm:p-8">
              <div className="font-overline text-[10px] text-black/60">{s.label}</div>
              <div className="mt-3 font-display text-4xl sm:text-5xl">{s.value ?? 0}</div>
            </div>
          ))}
        </div>

        <div className="border border-black/15 bg-white overflow-x-auto">
          <Table data-testid={ADMIN.table}>
            <TableHeader>
              <TableRow className="border-black/15">
                <TableHead className="font-overline text-[10px] text-black/60">{cols.name}</TableHead>
                <TableHead className="font-overline text-[10px] text-black/60">{cols.email}</TableHead>
                <TableHead className="font-overline text-[10px] text-black/60">{cols.attending}</TableHead>
                <TableHead className="font-overline text-[10px] text-black/60">{cols.guests}</TableHead>
                <TableHead className="font-overline text-[10px] text-black/60">{cols.meal}</TableHead>
                <TableHead className="font-overline text-[10px] text-black/60">{cols.song}</TableHead>
                <TableHead className="font-overline text-[10px] text-black/60">{cols.note}</TableHead>
                <TableHead className="font-overline text-[10px] text-black/60">{cols.submitted}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rsvps.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-16 font-serif italic text-black/50">
                    {t("admin.empty")}
                  </TableCell>
                </TableRow>
              )}
              {rsvps.map((r) => (
                <TableRow key={r.id} data-testid={ADMIN.row(r.id)} className="border-black/10">
                  <TableCell className="font-serif text-base">{r.full_name}</TableCell>
                  <TableCell className="text-sm text-black/70">{r.email}</TableCell>
                  <TableCell>
                    <span className={`inline-block px-2 py-1 font-overline text-[10px] ${
                      r.attending === "yes"
                        ? "bg-[color:var(--sunset-100)] text-[color:var(--sunset-700)]"
                        : "bg-black/5 text-black/60"
                    }`}>
                      {r.attending === "yes" ? t("admin.joyful") : t("admin.regrets")}
                    </span>
                  </TableCell>
                  <TableCell>{r.guest_count}</TableCell>
                  <TableCell className="capitalize text-sm">{r.meal_preference || "—"}</TableCell>
                  <TableCell className="text-sm italic text-black/70 max-w-[160px] truncate">{r.song_request || "—"}</TableCell>
                  <TableCell className="text-sm italic text-black/70 max-w-[220px] truncate">{r.message || "—"}</TableCell>
                  <TableCell className="text-xs text-black/50 whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                  <TableCell>
                    <button
                      data-testid={ADMIN.delete(r.id)}
                      onClick={() => remove(r.id)}
                      className="p-2 text-black/40 hover:text-[color:var(--sunset-600)] transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
